import { attrNames } from "@/modules/ExperienceAttributes";
import { readPathway } from "@/modules/pixieWaysIn";
import { resolveViewerSurface } from "@/modules/viewerSurface";
import type ExperienceAttributes from "@/modules/ExperienceAttributes";
import type { ChainParams } from "@/modules/ChainSession";

export interface ExperienceApiUrlOptions {
  publisherOrigin?: string;
  apiProtocol?: string;
  /** Host name only; overrides attrs.getApiBaseHost(). */
  apiBaseUrl?: string;
  versionQueryParam?: string;
}

export interface EdgeApiOptions {
  apiBaseUrl?: string;
  apiProtocol?: string;
}

/**
 * Where an assistant request comes from. `publisher` (the site's custom MCP
 * slug) is set on Pixie's own page on a publisher's site only.
 */
export interface AssistantSite {
  surface: string;
  publisher?: string;
}

/** Turn context sent as query params on /publisher/assistant/chat. */
export interface AssistantUrlContext {
  experienceId: unknown;
  publisher?: string;
  clientId: string;
  deviceKey?: string | null;
  chainSessionId?: unknown;
  source?: unknown;
  pathway?: unknown;
  positionS?: number | null;
  methodStep?: number | null;
  watchedRecipes?: { id: unknown; title: string }[];
  shownRecipeIds?: unknown[];
  publicationScope?: number[];
  layout?: string;
  surface: string;
  turnId?: string | null;
}

export interface ChainRequestBody {
  history: unknown[];
  exclude_ids: unknown[];
  limit?: number;
  device_key?: string;
  publication_scope?: number[];
  surface?: string;
}

/** Cache-buster query param; keep aligned with embed / player bootstrap. */
export const EXPERIENCE_API_VERSION_DEFAULT = "6574e01";

/**
 * Publisher page origin for pt= (protocol + host + port).
 */
export function getPublisherPageOrigin(loc?: Location | null): string {
  const L = loc || (typeof location !== "undefined" ? location : null);
  if (!L) {
    return "";
  }
  return `${L.protocol}//${L.hostname}${L.port ? `:${L.port}` : ""}`;
}

/**
 * Builds GET URL for /publisher/experience/:id with embed attrs + pt + v.
 */
export function buildPublisherExperienceApiUrl(
  attrs: ExperienceAttributes,
  opts?: ExperienceApiUrlOptions,
): string | null {
  opts = opts || {};
  const idexperience = attrs.getExperienceId();
  if (!idexperience) {
    return null;
  }

  const protocol =
    opts.apiProtocol ||
    (typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.apiProtocol) ||
    "https";
  const apiHost =
    opts.apiBaseUrl ||
    (typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.apiBaseUrl) ||
    attrs.getApiBaseHost();

  const publisherOrigin =
    opts.publisherOrigin !== undefined
      ? opts.publisherOrigin
      : getPublisherPageOrigin();

  const pairs = attrs.getAttributesForExperienceApiQuery();

  let qs = "";
  const add = function (k: string, v: unknown) {
    if (qs.length > 0) {
      qs += "&";
    }
    qs += `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`;
  };

  let k;
  for (k in pairs) {
    if (!Object.prototype.hasOwnProperty.call(pairs, k)) {
      continue;
    }
    add(k, pairs[k]);
  }

  add(attrNames.PARENT_URL, publisherOrigin);

  const v =
    opts.versionQueryParam ||
    (typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.experienceApiVersion) ||
    EXPERIENCE_API_VERSION_DEFAULT;
  if (v) {
    add("v", v);
  }

  return (
    protocol +
    "://" +
    apiHost +
    "/publisher/experience/" +
    encodeURIComponent(String(idexperience)) +
    "?" +
    qs
  );
}

/**
 * GET /publisher/experience?url=… — mapped publisher page URL (same qs extras as by-id).
 */
export function buildPublisherExperienceByUrlApiUrl(
  attrs: ExperienceAttributes,
  opts?: ExperienceApiUrlOptions,
): string | null {
  opts = opts || {};
  const pageUrl = attrs.getUrl();
  if (!pageUrl) {
    return null;
  }

  const protocol =
    opts.apiProtocol ||
    (typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.apiProtocol) ||
    "https";
  const apiHost =
    opts.apiBaseUrl ||
    (typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.apiBaseUrl) ||
    attrs.getApiBaseHost();

  const publisherOrigin =
    opts.publisherOrigin !== undefined
      ? opts.publisherOrigin
      : getPublisherPageOrigin();

  const pairs = attrs.getAttributesForExperienceApiQuery();

  let qs = "";
  const add = function (k: string, v: unknown) {
    if (qs.length > 0) {
      qs += "&";
    }
    qs += `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`;
  };

  add("url", pageUrl);

  let k;
  for (k in pairs) {
    if (!Object.prototype.hasOwnProperty.call(pairs, k)) {
      continue;
    }
    if (k === "url") {
      continue;
    }
    add(k, pairs[k]);
  }

  add(attrNames.PARENT_URL, publisherOrigin);

  const v =
    opts.versionQueryParam ||
    (typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.experienceApiVersion) ||
    EXPERIENCE_API_VERSION_DEFAULT;
  if (v) {
    add("v", v);
  }

  return `${protocol}://${apiHost}/publisher/experience?${qs}`;
}

/**
 * Build the POST URL for a public admin-api edge route:
 * <protocol>://<host>/publisher/<path>.
 *
 * Shared by /publisher/chain and /publisher/assistant/chat — both live on the
 * same admin-api origin the experience is fetched from. The authoritative
 * runtime source is window.pixmoto.experienceMetadata (populated by
 * player-boot, and the same global dataAccess reads to load the experience)
 * — NOT window.PIXMOTO_API_BASE_URL (optional, often unset) or
 * window.__pixmoto (an image-cache bucket). We derive the origin from the
 * already-resolved `experienceUrl` so every edge route always hits the same
 * host+protocol as the experience fetch. `opts.apiBaseUrl` (+ `apiProtocol`)
 * overrides for tests. Returns null if unresolvable.
 */
export function buildPublisherEdgeApiUrl(
  path: string,
  opts?: EdgeApiOptions,
): string | null {
  opts = opts || {};
  if (opts.apiBaseUrl) {
    return (
      (opts.apiProtocol || "https") +
      "://" +
      opts.apiBaseUrl +
      "/publisher/" +
      path
    );
  }
  const meta: { experienceUrl?: string } =
    (typeof window !== "undefined" &&
      window.pixmoto &&
      window.pixmoto.experienceMetadata) ||
    {};
  if (meta.experienceUrl) {
    try {
      return `${new URL(meta.experienceUrl).origin}/publisher/${path}`;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Build the POST URL for the public chain edge. Body-only request; no query
 * params. See buildPublisherEdgeApiUrl for the origin-resolution contract.
 */
export function buildPublisherChainApiUrl(opts?: EdgeApiOptions) {
  return buildPublisherEdgeApiUrl("chain", opts);
}

// The page's recipe. Pixie's page on a publisher's site may have none: the
// param is then omitted, which only that site's requests may do.
function setExperienceId(params: URLSearchParams, experienceId: unknown) {
  if (experienceId != null) {
    params.set("experience_id", String(experienceId));
  }
}

// A route whose query does not carry the surface otherwise names the site
// only on a publisher's own site, so the player's requests stay as they were.
function setSite(params: URLSearchParams, site: AssistantSite | undefined) {
  if (site && site.publisher) {
    params.set("surface", site.surface);
    params.set("publisher", site.publisher);
  }
}

/**
 * POST /publisher/assistant/chat — the Ask tab's streaming edge.
 * Turn context (experience_id, client_id, position_s, method_step) rides as
 * query params, appended here so the caller only needs to build the body.
 * `context.positionS`/`context.methodStep` are omitted (not sent as `null`)
 * when unknown — matching the admin-api edge's own "omit rather than null"
 * contract (pix-admin-api tests/unit/test_assistant_public.py).
 */
export function buildPublisherAssistantApiUrl(
  context: AssistantUrlContext,
  opts?: EdgeApiOptions,
): string | null {
  const base = buildPublisherEdgeApiUrl("assistant/chat", opts);
  if (!base) {
    return null;
  }
  const params = new URLSearchParams();
  setExperienceId(params, context.experienceId);
  params.set("client_id", context.clientId);
  // Durable per-device viewer profile key (Mixpanel $device_id) — distinct from
  // client_id, which is a per-browser continuity id. Omitted (not sent as the
  // literal "null") when Mixpanel hasn't initialized yet; the backend then falls
  // back to session-only personalisation.
  if (context.deviceKey != null) {
    params.set("device_key", context.deviceKey);
  }
  // The host's conversation key (the per-load ChainSession id on the player):
  // without it arcs-api groups every turn by the stable localStorage
  // client_id, collapsing all player loads in a browser into one ever-growing
  // conversation.
  if (context.chainSessionId) {
    params.set("chain_session_id", String(context.chainSessionId));
  }
  // How the viewer started this message: typed, a starter prompt, or an action
  // chip. The edge allowlists this param; anything else is dropped upstream.
  if (context.source) {
    params.set("source", String(context.source));
  }
  const pathway = readPathway(context.pathway);
  if (pathway) {
    params.set("pathway", pathway);
  }
  if (context.positionS != null) {
    params.set("position_s", String(context.positionS));
  }
  if (context.methodStep != null) {
    params.set("method_step", String(context.methodStep));
  }
  // Recipes watched earlier this session as `{id}|{title}`, one repeated
  // `watched_recipes` param each so a title containing a comma or pipe stays
  // intact (URLSearchParams encodes each value; the backend splits on the
  // first pipe, and the id is numeric). The edge trims and forwards them;
  // arcs-api pairs each id with its title in the turn context.
  if (context.watchedRecipes && context.watchedRecipes.length) {
    for (let i = 0; i < context.watchedRecipes.length; i++) {
      const watched = context.watchedRecipes[i];
      params.append("watched_recipes", `${watched.id}|${watched.title}`);
    }
  }
  // Recipe experience_ids already shown this session, one repeated
  // `shown_recipe_ids` param each. The edge caps and forwards them; arcs-api
  // excludes them from search so no card (or a sibling of one) comes back twice.
  if (context.shownRecipeIds && context.shownRecipeIds.length) {
    for (let j = 0; j < context.shownRecipeIds.length; j++) {
      params.append("shown_recipe_ids", String(context.shownRecipeIds[j]));
    }
  }
  // Custom-MCP publisher scope (?pix_scope=<idaccount,...> on this player's own
  // URL), one repeated `publication_scope` param each, so Pixie never suggests
  // a recipe outside the MCP's publishers.
  if (context.publicationScope && context.publicationScope.length) {
    context.publicationScope.forEach(function (id) {
      params.append("publication_scope", String(id));
    });
  }
  // Where Pixie runs. Fixed for the page, so every turn of the conversation
  // carries the same value; on the player, the one the chain request carries.
  params.set("surface", context.surface);
  if (context.publisher) {
    params.set("publisher", context.publisher);
  }
  // The workspace shows Pixie's cards in a six-slot grid, so arcs-api lets one
  // answer fill it. Every other layout omits the param and keeps three cards.
  if (context.layout === "workspace") {
    params.set("layout", "workspace");
  }
  // Makes the turn resumable; see buildPublisherAssistantTurnUrl.
  if (context.turnId) {
    params.set("turn_id", context.turnId);
  }
  return `${base}?${params.toString()}`;
}

/**
 * The routes that act on a turn already started with a `turn_id`:
 * `action` 'stream' re-attaches to its reply from event `after` (GET), and
 * 'stop' cancels it server-side (POST). Both answer only the `clientId` that
 * opened the turn. Returns null if the edge origin is unresolvable.
 */
export function buildPublisherAssistantTurnUrl(
  turnId: string,
  action: "stream" | "stop",
  params: { clientId: string; after?: unknown },
  opts?: EdgeApiOptions,
): string | null {
  const base = buildPublisherEdgeApiUrl(
    `assistant/chat/${encodeURIComponent(turnId)}/${action}`,
    opts,
  );
  if (!base) {
    return null;
  }
  const query = new URLSearchParams();
  query.set("client_id", params.clientId);
  if (params.after != null) {
    query.set("after", String(params.after));
  }
  return `${base}?${query.toString()}`;
}

/**
 * POST /publisher/assistant/voice/offer — answers the browser's SDP offer with
 * the SDP answer and a call id. The call's context rides in the body, except
 * `surface`, a query param as on /chat. See buildPublisherEdgeApiUrl.
 */
export function buildPublisherVoiceOfferUrl(
  context: AssistantSite,
  opts?: EdgeApiOptions,
): string | null {
  const base = buildPublisherEdgeApiUrl("assistant/voice/offer", opts);
  if (!base) {
    return null;
  }
  const params = new URLSearchParams();
  params.set("surface", context.surface);
  if (context.publisher) {
    params.set("publisher", context.publisher);
  }
  return `${base}?${params.toString()}`;
}

/**
 * The routes that act on a voice call already offered: `action` 'events'
 * streams the call's panel events (GET, SSE), 'say' submits a tapped reply
 * (POST) and 'end' hangs it up (POST). The edge repeats its guards on every
 * route, so each one names the experience, and on a publisher's own site the
 * site. Returns null if the edge origin is unresolvable.
 */
export function buildPublisherVoiceCallUrl(
  callId: string,
  action: "events" | "say" | "end",
  params: { experienceId: unknown; site?: AssistantSite },
  opts?: EdgeApiOptions,
): string | null {
  const base = buildPublisherEdgeApiUrl(
    `assistant/voice/${encodeURIComponent(callId)}/${action}`,
    opts,
  );
  if (!base) {
    return null;
  }
  const query = new URLSearchParams();
  setExperienceId(query, params.experienceId);
  setSite(query, params.site);
  return `${base}?${query.toString()}`;
}

/**
 * GET /publisher/assistant/starters — the Ask tab's opening prompts.
 * `experience_id` is sent whenever the page has a recipe: off a publisher's
 * own site the edge requires it (it gates the call on that publisher's Pixie
 * opt-in, the same consent the chat edge checks) and answers 400 without it.
 * `device_key` is omitted rather than sent as the literal "null" when Mixpanel
 * hasn't initialized yet: the edge builds its upstream params as an allowlist
 * and would forward the string. A keyless request is fine — a device with no
 * durable profile gets exactly what a keyed one would.
 */
export function buildPublisherStartersApiUrl(
  context: {
    experienceId: unknown;
    deviceKey?: string | null;
    site?: AssistantSite;
  },
  opts?: EdgeApiOptions,
): string | null {
  const base = buildPublisherEdgeApiUrl("assistant/starters", opts);
  if (!base) {
    return null;
  }
  const params = new URLSearchParams();
  setExperienceId(params, context.experienceId);
  if (context.deviceKey != null) {
    params.set("device_key", context.deviceKey);
  }
  setSite(params, context.site);
  return `${base}?${params.toString()}`;
}

/**
 * GET /publisher/assistant/availability — load-time gate for the Pixie tab
 *. `experience_id` gates on the publisher's opt-in; the edge adds
 * the global 24h spend-ceiling check. The player only mounts the tab on
 * `{available: true}`, so a viewer who just watches sees no Pixie once the
 * day's budget is spent. Global only — the per-visitor throttle degrades
 * mid-chat instead. On a publisher's own site the edge gates the site instead
 * and the recipe is optional page context.
 */
export function buildPublisherAssistantAvailabilityApiUrl(
  context: { experienceId: unknown; site?: AssistantSite },
  opts?: EdgeApiOptions,
): string | null {
  const base = buildPublisherEdgeApiUrl("assistant/availability", opts);
  if (!base) {
    return null;
  }
  const params = new URLSearchParams();
  setExperienceId(params, context.experienceId);
  setSite(params, context.site);
  return `${base}?${params.toString()}`;
}

/**
 * Shape the /chain request body. The currently-playing experience is the seed:
 * appended as the most-recent history entry and added to the exclude set so it
 * is never recommended back. `chainParams` is ChainSession.toChainParams().
 */
export function buildChainRequestBody(
  currentId: unknown,
  chainParams?: ChainParams | null,
  limit?: number | null,
  deviceKey?: string | null,
): ChainRequestBody {
  const params: ChainParams = chainParams || { history: [], exclude_ids: [] };
  const body: ChainRequestBody = {
    history: (params.history as unknown[]).concat([currentId]),
    exclude_ids: Array.from(
      new Set((params.exclude_ids as unknown[]).concat([currentId])),
    ),
  };
  // Only send `limit` when a caller explicitly passes one. Omitting it lets the
  // number of recommendations come from the global chain config:
  // admin-api forwards nothing, so the engine falls back to the config's limit
  //. Sending a value here would override that config.
  if (limit != null) {
    body.limit = limit;
  }
  // Durable per-device viewer profile key (Mixpanel $device_id). Omitted when
  // Mixpanel hasn't initialized yet; reranking then falls back to this
  // request's own history/exclude_ids only.
  if (deviceKey != null) {
    body.device_key = deviceKey;
  }
  // Custom-MCP publisher scope, carried in with the other chain params.
  // Omitted off the MCP surface so the engine's normal (unscoped) ranking
  // applies.
  if (params.publication_scope && params.publication_scope.length) {
    body.publication_scope = params.publication_scope;
  }
  // Define where the player is
  body.surface = resolveViewerSurface();
  return body;
}
